import { ref, computed } from 'vue'

/** Match BaseScrollbar rail threshold so we ignore “overflow” noise */
const HORIZONTAL_OVERFLOW_THRESHOLD_PX = 4

let container: HTMLElement | null = null
let scrollListener: (() => void) | null = null
let resizeObserver: ResizeObserver | null = null
let pendingResizeRafId: number | null = null

const scrollLeft = ref(0)
const scrollWidth = ref(0)
const clientWidth = ref(0)
const hasRegistration = ref(false)

const visible = computed(
  () => hasRegistration.value && scrollWidth.value > clientWidth.value
)

function updateFromContainer() {
  if (!container) return
  scrollLeft.value = container.scrollLeft
  scrollWidth.value = container.scrollWidth
  clientWidth.value = container.clientWidth
}

function elementIsHorizontalScrollport(el: HTMLElement): boolean {
  const ox = window.getComputedStyle(el).overflowX
  if (ox !== 'auto' && ox !== 'scroll') return false
  return el.scrollWidth - el.clientWidth > HORIZONTAL_OVERFLOW_THRESHOLD_PX
}

/**
 * First (innermost) element on the event path that can scroll horizontally.
 * Used so nested cards/tables take priority over the page sticky bar (same idea as vertical scroll chaining).
 */
export function findInnermostHorizontalScrollportFromWheelEvent(e: WheelEvent): HTMLElement | null {
  const path = e.composedPath()
  for (const node of path) {
    if (node === document || node === window) break
    if (!(node instanceof HTMLElement)) continue
    if (elementIsHorizontalScrollport(node)) return node
  }
  return null
}

/**
 * Whether the sticky bottom horizontal bar should handle Shift+wheel for the registered table.
 * False when a nested horizontal scrollport under the pointer should own the gesture instead.
 */
export function shouldStickyBarClaimHorizontalWheel(e: WheelEvent): boolean {
  if (!container) return false
  const inner = findInnermostHorizontalScrollportFromWheelEvent(e)
  if (!inner) return true
  return inner === container
}

/**
 * Composable for sticky horizontal scroll bar: table wrappers register when they
 * have horizontal overflow; clear on unmount. StickyHorizontalScrollBar reads state.
 */
export function useStickyHorizontalScroll() {
  function register(el: HTMLElement) {
    clear()
    container = el
    hasRegistration.value = true
    updateFromContainer()

    scrollListener = () => updateFromContainer()
    container.addEventListener('scroll', scrollListener, { passive: true })

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (pendingResizeRafId != null) cancelAnimationFrame(pendingResizeRafId)
        pendingResizeRafId = requestAnimationFrame(() => {
          pendingResizeRafId = null
          updateFromContainer()
        })
      })
      resizeObserver.observe(container)
    }
  }

  function clear(unregisteringEl?: HTMLElement | null) {
    const el = container
    if (unregisteringEl != null && el !== unregisteringEl) return
    if (pendingResizeRafId != null) {
      cancelAnimationFrame(pendingResizeRafId)
      pendingResizeRafId = null
    }
    if (el && scrollListener) {
      el.removeEventListener('scroll', scrollListener)
    }
    if (resizeObserver && el) {
      resizeObserver.unobserve(el)
    }
    resizeObserver = null
    scrollListener = null
    container = null
    hasRegistration.value = false
    scrollLeft.value = 0
    scrollWidth.value = 0
    clientWidth.value = 0
  }

  function setScrollLeft(value: number) {
    if (!container) return
    const max = Math.max(0, container.scrollWidth - container.clientWidth)
    const clamped = Math.max(0, Math.min(max, value))
    container.scrollLeft = clamped
    scrollLeft.value = container.scrollLeft
  }

  function handleWheel(shiftKey: boolean, deltaX: number, deltaY: number) {
    if (!shiftKey || !container) return
    const deltaH = deltaX !== 0 ? deltaX : -deltaY
    setScrollLeft(scrollLeft.value + deltaH)
  }

  return {
    scrollLeft,
    scrollWidth,
    clientWidth,
    visible,
    register,
    clear,
    setScrollLeft,
    handleWheel,
    shouldStickyBarClaimHorizontalWheel
  }
}
